/**
 * Utility for replacing {{key}} placeholders with values from a variables map
 */

import { IFormPageData } from '../models/form-page-data';

export type VariablesMap = Record<string, any>;

/**
 * Replaces all occurrences of {{key}} with corresponding values from the variables map
 * @param text - The text containing placeholders
 * @param variables - Key-value pairs for replacement
 * @returns The text with all placeholders replaced
 */
export function replaceVariables(text: string | undefined | null, variables: VariablesMap): string {
    if (!text) {
        return '';
    }

    // Match all {{key}} patterns
    const pattern = /\{\{([^}]+)\}\}/g;

    return text.replace(pattern, (match, key) => {
        const trimmedKey = key.trim();
        if (variables.hasOwnProperty(trimmedKey)) {
            const value = variables[trimmedKey];
            return value !== undefined && value !== null ? String(value) : match;
        }

        return '';
    });
}

/**
 * Deep clone an object with variable replacement in string values
 */
function cloneWithVariableReplacement<T>(obj: T, variables: VariablesMap): T {
    if (obj === null || obj === undefined) {
        return obj;
    }

    if (typeof obj === 'string') {
        return replaceVariables(obj, variables) as any;
    }

    if (Array.isArray(obj)) {
        return obj.map(item => cloneWithVariableReplacement(item, variables)) as any;
    }

    if (typeof obj === 'object') {
        const result: any = {};
        for (const key of Object.keys(obj)) {
            result[key] = cloneWithVariableReplacement((obj as any)[key], variables);
        }
        return result;
    }

    return obj;
}

/**
 * Apply variable replacement to form page data
 * This creates a deep clone with all string values having {{key}} replaced
 */
export function applyVariablesToPageData(data: IFormPageData, variables: VariablesMap): any {
    if (!variables || Object.keys(variables).length === 0) {
        return data;
    }

    const result = cloneWithVariableReplacement(data, variables);

    removeEmptyWidgets(result);

    return result;
}

function removeEmptyWidgets(page: IFormPageData): void {
    page.sections?.forEach((section: any) => {
        section.meshes?.forEach((mesh: any) => {
            mesh.rows?.forEach((row: any) => {
                row.columns?.forEach((column: any) => {
                    column.elements = column.elements?.filter(shouldKeepElement) ?? [];
                });
            });
        });
    });
}

function shouldKeepElement(element: any): boolean {
    if (element.type !== 'widget') {
        return true;
    }

    const widget = element.data;

    if (!widget || typeof widget !== 'object') {
        return true;
    }

    if (typeof widget.content === 'string') {
        return !isHtmlEmpty(widget.content);
    }

    return true;
}

function isHtmlEmpty(html: string): boolean {
    if (!html.trim()) {
        return true;
    }

    const container = document.createElement('div');
    container.innerHTML = html;

    if (container.textContent?.trim()) {
        return false;
    }

    return !container.querySelector(
        'img,svg,video,audio,iframe,canvas,table,hr,ul,ol,blockquote'
    );
}
